import React, {useCallback, useEffect, useRef} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {Form, FormInput, SubmitBehavior, SubmitButton} from '@modifi/plugin-forms'
import {
  ButtonPrimary,
  Col,
  DatePickerPure,
  Row,
  withInputContainer,
  H6,
  Regular,
  CaptionBlock,
  SkeletonButton,
  SkeletonLine,
} from '@modifi/ui'
import {DocumentType} from '@modifi/enums'
import {useTranslation} from '@modifi/plugin-i18n'
import {mediumGray} from '@modifi/colors'
import ShipmentDocumentUploadForm from './shipment-document-upload-form'
import {Separator, SeparatorWrapper, Wrapper} from './styles'
import {INITIAL_FORM_DATA, INPUT_CONTAINER_ID, INPUT_SHIPMENT_START_DATE} from './constants'
import {validateContainerId} from './utils'
import {FormInputCapitalized} from '../../../../components'
import {BOL_PARSE_FAIL, BOL_PARSE_SUCCESS} from '../../../../constants/tagManager'

const availableDocumentTypes = [DocumentType.BILL_OF_LADING]

const DatePickerPureWithContainer = withInputContainer(DatePickerPure)

const EMPTY_OBJECT = {}

const propTypes = {
  submit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  submitPending: PropTypes.bool.isRequired,
  submitHasError: PropTypes.bool.isRequired,
  setSubmitStrategy: PropTypes.func.isRequired,
  triggerEvent: PropTypes.func.isRequired,
}
const defaultProps = {}

export type SubmitShipmentProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const SubmitShipment: React.FC<SubmitShipmentProps> = ({
  submit,
  reset,
  setSubmitStrategy,
  submitPending,
  submitHasError,
  triggerEvent,
}) => {
  const {t, ready: tReady} = useTranslation(['argus', 'plugin-forms'], {useSuspense: false})
  const formRef = useRef()

  const handleSubmit = useCallback(
    (data: Record<string, any>) => {
      setSubmitStrategy('FORM')
      submit(data)
    },
    [submit, setSubmitStrategy]
  )

  const handleFileUploading = useCallback(
    (file: {fileIdent: string}) => {
      setSubmitStrategy('DOCUMENT')
      submit({fileIdent: file.fileIdent})
    },
    [submit, setSubmitStrategy]
  )

  const handleFileUploaded = useCallback(
    (
      file: {fileId: string; documentType: string},
      extractedContainerIds: string[],
      shipmentStartDate: string
    ) => {
      const isDocumentParsed = !!extractedContainerIds.length && !!shipmentStartDate
      triggerEvent({
        action: isDocumentParsed ? BOL_PARSE_SUCCESS : BOL_PARSE_FAIL,
        label: isDocumentParsed ? 'BOL parsed successfully' : 'BOL parsing failed',
      })
      setSubmitStrategy('DOCUMENT')
      submit({
        fileId: file.fileId,
        documentType: file.documentType,
        extractedContainerIds,
        shipmentStartDate,
      })
    },
    [triggerEvent, setSubmitStrategy, submit]
  )

  const performContainerIdValidation = useCallback((value: string) => {
    const containerIds = value.split(',')
    for (let i = 0; i < containerIds.length; i += 1) {
      const result = validateContainerId(containerIds[i])
      if (result) {
        return result
      }
    }
    return undefined
  }, [])

  const prevSubmitPending = useRef(submitPending)
  useEffect(() => {
    if (prevSubmitPending.current !== submitPending) {
      prevSubmitPending.current = submitPending

      if (!submitPending && !submitHasError) {
        // @ts-expect-error wrong typing
        formRef?.current?.formDataRef?.current?.discardData?.()
      }
    }
  }, [submitPending, submitHasError])

  return (
    <Wrapper>
      <Row>
        <Col col={5}>
          <H6 my={0}>{tReady ? t('submit-shipment-form.upload.headline') : <SkeletonLine />}</H6>
          <CaptionBlock>
            {tReady ? (
              t('submit-shipment-form.upload.description')
            ) : (
              <SkeletonLine stringLength={40} />
            )}
          </CaptionBlock>
          <ShipmentDocumentUploadForm
            availableDocumentTypes={availableDocumentTypes}
            isDisabled={submitPending}
            handleFileUploading={handleFileUploading}
            handleFileUploaded={handleFileUploaded}
          />
        </Col>
        <Col col={1} noGutter>
          <SeparatorWrapper>
            <Separator />
            {tReady && (
              <>
                <Regular color={mediumGray}>{t('submit-shipment-form.separator')}</Regular>
                <Separator />
              </>
            )}
          </SeparatorWrapper>
        </Col>
        <Col col>
          <H6 my={0}>
            {tReady ? (
              t('submit-shipment-form.search.headline')
            ) : (
              <SkeletonLine stringLength={15} />
            )}
          </H6>
          <CaptionBlock>
            {tReady ? (
              t('submit-shipment-form.search.description')
            ) : (
              <SkeletonLine stringLength={45} />
            )}
          </CaptionBlock>
          <Form
            // @ts-expect-error wrong typing
            ref={formRef}
            onSubmit={handleSubmit}
            onDiscard={reset}
            submitBehavior={SubmitBehavior.SUBMIT_DISABLED_UNLESS_VALID}
            initialValues={INITIAL_FORM_DATA}
            defaultValues={EMPTY_OBJECT}
          >
            <Row>
              <Col xl={4}>
                <FormInputCapitalized
                  name={INPUT_CONTAINER_ID}
                  isSmall
                  placeholder={tReady ? t('submit-shipment-form.container-id.label') : ''}
                  collapse
                  required
                  disabled={submitPending}
                  validationFunction={performContainerIdValidation}
                />
              </Col>
              <Col xl={5}>
                <FormInput
                  inputComponent={DatePickerPureWithContainer}
                  name={INPUT_SHIPMENT_START_DATE}
                  fromDate={false}
                  isSmall
                  t={t}
                  placeholder={tReady ? t('submit-shipment-form.shipment-start-date.label') : ''}
                  collapse
                  disabled={submitPending}
                  required
                />
              </Col>
              <Col xl="auto" ml="auto" pt={4}>
                {tReady ? (
                  <SubmitButton
                    buttonComponent={ButtonPrimary}
                    data-target="submit-new-shipment"
                    active={!submitPending}
                  >
                    {t('submit-shipment-form.search.cta')}
                  </SubmitButton>
                ) : (
                  <SkeletonButton>
                    <SubmitButton
                      buttonComponent={ButtonPrimary}
                      data-target="submit-new-shipment"
                      active={!submitPending}
                    >
                      Submit
                    </SubmitButton>
                  </SkeletonButton>
                )}
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Wrapper>
  )
}

SubmitShipment.propTypes = propTypes
SubmitShipment.defaultProps = defaultProps

export default SubmitShipment
